import type { AxiosRequestConfig } from 'axios';
import { createApiClient } from '@bookora/ui';
import type { TokenStore } from '@bookora/ui';

import {
  broadcastAuthExpired,
  clearSession,
  getSessionSnapshot,
  setTokens,
  useSessionStore,
} from '@/auth/token-store';
import { env } from '@/lib/env';

/**
 * The one HTTP client. Everything — the hand-written callers in
 * `src/api/endpoints/`, and every function orval generates — goes through here.
 *
 * ── WHY THE TRANSPORT ITSELF LIVES IN `@bookora/ui` ─────────────────────────
 *
 * Version prefixing, `{ data }` unwrapping, Persian error translation and —
 * above all — the SINGLE-FLIGHT REFRESH are identical in this panel and in the
 * super-admin console, and they used to be written twice. Two refresh
 * implementations is not a tidiness problem: the backend rotates the refresh
 * token on every use and treats a reused one as theft, so the moment the two
 * copies disagree about how many refreshes a burst of 401s should produce, one
 * of the panels starts signing its users out mid-shift for no visible reason.
 * `createApiClient` is that logic, once, with the tests to match.
 *
 * What stays here is only what is genuinely this panel's: which store holds the
 * session, where an expiry is announced, and how long a clinic on a slow
 * connection is allowed to wait.
 */

/**
 * The kernel wants a tokens-only observable store; this panel's session store
 * also carries the user, memberships and the affordance cache, because those
 * arrive on the same response and must be dropped at the same instant.
 * The adapter is the seam between the two.
 */
const tokens: TokenStore = {
  getSnapshot: () => {
    const { accessToken, refreshToken } = getSessionSnapshot();
    return { accessToken, refreshToken };
  },
  set: (pair) => {
    setTokens(pair);
  },
  clear: () => {
    clearSession();
  },
  subscribe: (listener) =>
    useSessionStore.subscribe((state, previous) => {
      if (
        state.accessToken !== previous.accessToken ||
        state.refreshToken !== previous.refreshToken
      ) {
        listener();
      }
    }),
};

export const api = createApiClient({
  /** `env.apiBaseUrl` carries no `/v1`; the client appends it exactly once. */
  baseUrl: env.apiBaseUrl,
  tokens,
  /**
   * A clinic panel on an Iranian connection waits longer than the console
   * does: a photo-heavy patient file over a congested link is slow but not
   * broken, and a premature timeout looks like an outage.
   */
  timeoutMs: 30_000,
  headers: () => ({
    Accept: 'application/json',
    // The API answers in Persian regardless; this is here so a future
    // multi-locale endpoint has something to read rather than guessing.
    'Accept-Language': 'fa-IR',
  }),
  /**
   * Fired when the session is unrecoverable. An event rather than a direct
   * import so the transport layer stays free of router knowledge —
   * `AuthProvider` listens and navigates.
   */
  onSessionExpired: (reason) => {
    broadcastAuthExpired(reason);
  },
});

/** The raw axios instance — multipart uploads and blob downloads need it. */
export const http = api.http;

/**
 * Orval's mutator, and the function every hand-written caller uses.
 * Returns the payload with the `{ data }` envelope removed.
 */
export function apiClient<T>(config: AxiosRequestConfig): Promise<T> {
  return api.request<T>(config);
}

/** GET helper — the shape most callers want. */
export function apiGet<T>(url: string, params?: unknown, signal?: AbortSignal): Promise<T> {
  return apiClient<T>({ url, method: 'GET', params, signal });
}

export function apiPost<T>(url: string, data?: unknown, signal?: AbortSignal): Promise<T> {
  return apiClient<T>({ url, method: 'POST', data, signal });
}

export function apiPatch<T>(url: string, data?: unknown): Promise<T> {
  return apiClient<T>({ url, method: 'PATCH', data });
}

export function apiPut<T>(url: string, data?: unknown): Promise<T> {
  return apiClient<T>({ url, method: 'PUT', data });
}

export function apiDelete<T>(url: string, data?: unknown): Promise<T> {
  return apiClient<T>({ url, method: 'DELETE', data });
}
